// Dữ liệu hai cột trang chủ theo nhóm: API thật (lịch hẹn, hiệu suất sale / telesale, thông báo) + thẻ liệu trình, lần đến lưu trên máy.
// Phần chưa có API (chấm công, phép, khi API lỗi) dùng số mẫu ở môi trường dev và gắn nhãn "Mẫu".
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getAppointments, type AppointmentItem } from 'api/Appointment';
import { getSaleBranchPerformanceV2, getTelesalePerformanceV2, type SaleBranchPerformanceData, type TelesalePerformanceData } from 'api/DashboardStatistics';
import { getNotifications } from 'api/Notification';
import useTreatmentStore from '../../Treatment/store';
import useC360Store from '../../Customer360/store';
import { useUnsignedVisitRows } from '../../Customer360/VisitWidgets';
import type { DashboardUser } from '../component/user';
import { cardSignals, moneyShort, type HomeRole, type RoleTarget } from './homeData';

export const HOME_DEMO = process.env.NODE_ENV !== 'production';

export type Tone = 'danger' | 'warn' | 'info' | 'ok';
/** Một việc cần làm: bấm để mở danh sách đã lọc sẵn */
export interface HomeTodo { key: string; icon: string; label: string; count: number; extra?: string; to: string; tone: Tone }
export interface HomeRow { at: string; title: string; sub: string; to: string }
export interface HomeStat { label: string; value: string; to?: string }
export interface HomeKpi { label: string; value: number; target: number; unit: string; demo?: boolean }
export interface HomeMetric { label: string; text: string; pct: number | null; demo?: boolean }
export interface HomeData {
  todos: HomeTodo[];
  /** Ô số nhanh đầu danh sách việc (VD lịch hôm nay, doanh thu cơ sở) */
  stats: HomeStat[];
  statsDemo?: boolean;
  schedule: { title: string; rows: HomeRow[]; demo?: boolean } | null;
  kpi: HomeKpi | null;
  metrics: HomeMetric[];
  note: string;
  noteDemo?: boolean;
  loading: boolean;
}

interface Remote {
  appts: AppointmentItem[] | null;
  saleMonth: SaleBranchPerformanceData | null;
  saleToday: SaleBranchPerformanceData | null;
  teleMonth: TelesalePerformanceData | null;
  unreadTele: number | null;
}
const EMPTY: Remote = { appts: null, saleMonth: null, saleToday: null, teleMonth: null, unreadTele: null };

const ok = <T,>(p: Promise<{ status: boolean; data?: T | null }>) => p.then(r => (r.status && r.data ? r.data : null)).catch(() => null);
const apptTo = (a: AppointmentItem) => (a.customer_tag_page === 3 ? '/execution-schedule' : a.customer_tag_page === 2 ? '/consultations' : '/appointments');
const toRows = (list: AppointmentItem[]): HomeRow[] => list.slice().sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at)).map(a => ({
  at: a.scheduled_at,
  title: a.customer_name || a.channel_customer?.display_name || 'Khách',
  sub: [a.services?.map(s => s.name).join(', ') || a.service?.name, a.customer_tag?.name].filter(Boolean).join(' · '),
  to: apptTo(a),
}));
const demoRows = (titles: [string, string][]): HomeRow[] => titles.map(([title, sub], i) => ({
  at: dayjs().startOf('hour').add(i * 90 - 60, 'minute').toISOString(), title, sub, to: '/appointments',
}));

export function useHomeData(role: HomeRole, user: DashboardUser, target: RoleTarget): HomeData {
  const [remote, setRemote] = useState<Remote>(EMPTY);
  const [loading, setLoading] = useState(true);
  const cards = useTreatmentStore(s => s.cards);
  const payRequests = useTreatmentStore(s => s.payRequests);
  const tcfg = useTreatmentStore(s => s.config);
  const c360docs = useC360Store(s => s.docs);
  const photos = useC360Store(s => s.photos);
  const records = useC360Store(s => s.visitRecords);
  const { rows: unsigned, signDays } = useUnsignedVisitRows(30);

  const today = dayjs().format('YYYY-MM-DD');
  const monthStart = dayjs().startOf('month').format('YYYY-MM-DD');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      getAppointments({ date_from: today, date_to: today, limit: 200 }).then(r => (r.status ? r.data?.items || [] : null)).catch(() => null),
      role === 'sale' || role === 'manager' ? ok(getSaleBranchPerformanceV2(monthStart, today)) : null,
      role === 'manager' ? ok(getSaleBranchPerformanceV2(today, today)) : null,
      role === 'telesale' ? ok(getTelesalePerformanceV2(monthStart, today)) : null,
      role === 'telesale' ? getNotifications({ is_read: false, type: 'noti_telesale', limit: 1 }).then(r => (r.status && r.data ? r.data.total : null)) : null,
    ]).then(([appts, saleMonth, saleToday, teleMonth, unreadTele]) => {
      if (!alive) return;
      setRemote({ appts, saleMonth, saleToday, teleMonth, unreadTele });
      setLoading(false);
    });
    return () => { alive = false; };
  }, [role, today, monthStart]);

  return useMemo(() => {
    const me = user.staffId;
    const myName = user.fullname.trim().toLowerCase();
    const isMine = (n?: string) => !!myName && (n || '').trim().toLowerCase() === myName;
    // Thẻ do mình bán / tư vấn; chưa có thẻ nào khớp tên thì xem toàn cơ sở
    const myCards = cards.filter(c => isMine(c.consultant) || isMine(c.soldBy));
    const sig = cardSignals(role === 'sale' && myCards.length ? myCards : cards, payRequests, tcfg);
    const appts = remote.appts;
    const mineAppts = (pick: (a: AppointmentItem) => number | null | undefined) => (appts ? (me ? appts.filter(a => pick(a) === me) : appts) : null);
    const unsignedLate = unsigned.filter(u => u.v.overdue).length;

    const d: HomeData = { todos: [], stats: [], schedule: null, kpi: null, metrics: [], note: '', loading };
    const todo = (key: string, icon: string, label: string, count: number, to: string, tone: Tone, extra?: string) => {
      if (count > 0) d.todos.push({ key, icon, label, count, to, tone, extra });
    };
    const kpiFrom = (value: number | null, demoValue: number) => {
      if (value !== null) d.kpi = { label: target.label, value, target: target.value, unit: target.unit };
      else if (HOME_DEMO) d.kpi = { label: target.label, value: demoValue, target: target.value, unit: target.unit, demo: true };
    };
    const byPage = (list: AppointmentItem[], p: number) => list.filter(a => a.customer_tag_page === p).length;

    if (role === 'sale') {
      const mine = mineAppts(a => a.branch_staff?.id ?? a.staff?.id);
      if (mine) {
        d.stats = [
          { label: 'Lịch hẹn', value: String(byPage(mine, 1) || mine.filter(a => !a.customer_tag_page).length), to: '/appointments' },
          { label: 'Tư vấn', value: String(byPage(mine, 2)), to: '/consultations' },
          { label: 'Thực hiện', value: String(byPage(mine, 3)), to: '/execution-schedule' },
        ];
        d.schedule = { title: 'Lịch hôm nay', rows: toRows(mine) };
      } else if (HOME_DEMO) {
        d.stats = [{ label: 'Lịch hẹn', value: '4' }, { label: 'Tư vấn', value: '6' }, { label: 'Thực hiện', value: '2' }];
        d.statsDemo = true;
        d.schedule = { title: 'Lịch hôm nay', rows: demoRows([['Nguyễn Thu Hà', 'Tư vấn trị nám'], ['Lê Minh Anh', 'Báo giá căng chỉ'], ['Trần Bảo Ngọc', 'Tái khám']]), demo: true };
      }
      todo('overdue', 'fa-solid fa-hand-holding-dollar', 'Thẻ nợ quá hạn – gọi nhắc khách', sig.overdue, '/treatment/debts', 'danger', moneyShort(sig.overdueAmount));
      todo('due', 'fa-solid fa-calendar-xmark', 'Đợt thu sắp đến hạn', sig.dueSoon, '/treatment/debts', 'warn');
      todo('pay', 'fa-solid fa-cash-register', 'Yêu cầu thu đang chờ POS', sig.pendingPay, '/treatment', 'info');
      todo('low', 'fa-solid fa-battery-quarter', 'Thẻ sắp hết buổi – tư vấn mua tiếp', sig.low, '/treatment/cards?warn=low', 'ok');
      todo('expiring', 'fa-solid fa-hourglass-half', 'Thẻ sắp hết hạn', sig.expiring, '/treatment/cards?warn=expiring', 'warn');
      const r = remote.saleMonth?.rankings.find(x => me && x.staff_id === me);
      kpiFrom(r ? r.revenue : null, 312_000_000);
      if (r) {
        d.metrics.push({ label: 'Tỉ lệ chốt', text: `${r.closed}/${r.showup} khách · ${Math.round(r.rate)}%`, pct: Math.round(r.rate) });
        d.note = `Hạng ${r.rank}/${remote.saleMonth!.rankings.length} sale cơ sở`;
      } else if (HOME_DEMO) {
        d.metrics.push({ label: 'Tỉ lệ chốt', text: '18/31 khách · 58%', pct: 58, demo: true });
        d.note = 'Hạng 3/12 sale cơ sở'; d.noteDemo = true;
      }
    }

    if (role === 'doctor') {
      const exec = mineAppts(a => a.action_staff?.id)?.filter(a => a.customer_tag_page === 3 || !!a.action_staff) ?? null;
      d.stats = [
        { label: 'Ca thực hiện', value: exec ? String(exec.length) : '–', to: '/execution-schedule' },
        { label: 'Đặt trừ buổi', value: String(sig.bookingsToday), to: '/treatment/checkin' },
        { label: 'Đã làm', value: String(sig.usagesToday), to: '/treatment/checkin' },
      ];
      if (exec) d.schedule = { title: 'Ca hôm nay', rows: toRows(exec) };
      else if (HOME_DEMO) d.schedule = { title: 'Ca hôm nay', rows: demoRows([['Phạm Thảo', 'Laser trị nám · buổi 4/10'], ['Võ Hồng Nhung', 'Căng chỉ collagen'], ['Đỗ Mai', 'Tái khám D+7']]), demo: true };
      const active = cards.filter(c => c.status === 'active' && c.usages.some(u => !u.cancelled));
      const consentOf = new Set(c360docs.filter(x => x.kind === 'consent' && x.status === 'signed').map(x => x.posCustomerId));
      const beforeOf = new Set(photos.filter(p => p.phase === 'before' && !p.hidden && p.cardId).map(p => p.cardId));
      todo('late', 'fa-solid fa-signature', `Lần đến chưa ký quá ${signDays} ngày`, unsignedLate, '/treatment', 'danger');
      todo('sign', 'fa-solid fa-pen-nib', 'Lần đến chờ khách ký', unsigned.length - unsignedLate, '/treatment', 'warn');
      todo('consent', 'fa-solid fa-file-signature', 'Khách chưa ký phiếu đồng ý', new Set(active.filter(c => !consentOf.has(c.customerId)).map(c => c.customerId)).size, '/treatment/cards', 'warn');
      todo('photo', 'fa-solid fa-camera', 'Thẻ chưa có ảnh "trước"', active.filter(c => !beforeOf.has(c.id)).length, '/treatment/cards', 'info');
      const monthUsages = cards.flatMap(c => c.usages).filter(u => !u.cancelled && u.at >= monthStart);
      const myUsages = monthUsages.filter(u => (me && u.staffId === String(me)) || isMine(u.by));
      const used = myUsages.length ? myUsages : monthUsages;
      kpiFrom(used.length || !HOME_DEMO ? used.length : null, 64);
      const rated = records.filter(r => r.rating && r.date >= monthStart);
      if (rated.length) {
        const avg = rated.reduce((s, r) => s + (r.rating || 0), 0) / rated.length;
        d.metrics.push({ label: 'Khách đánh giá', text: `${avg.toFixed(1).replace('.', ',')}/5 · ${rated.length} lượt`, pct: Math.round((avg / 5) * 100) });
      } else if (HOME_DEMO) d.metrics.push({ label: 'Khách đánh giá', text: '4,8/5 · 41 lượt', pct: 96, demo: true });
      d.note = myUsages.length ? 'Tính theo lượt trừ buổi bạn làm' : used.length ? 'Tính theo lượt trừ buổi toàn cơ sở' : '';
    }

    if (role === 'telesale') {
      const mine = mineAppts(a => a.telesale_id ?? a.telesale?.id);
      d.stats = [
        { label: 'Lịch tôi đặt', value: mine ? String(mine.length) : HOME_DEMO ? '7' : '–', to: '/appointments' },
        { label: 'Lead chưa đọc', value: remote.unreadTele !== null ? String(remote.unreadTele) : HOME_DEMO ? '14' : '–', to: '/notifications' },
        { label: 'Cần giữ chân', value: String(sig.idle + sig.low), to: '/treatment/cards?status=active' },
      ];
      d.statsDemo = HOME_DEMO && (!mine || remote.unreadTele === null);
      if (mine) d.schedule = { title: 'Khách hẹn đến hôm nay', rows: toRows(mine) };
      todo('idle', 'fa-solid fa-user-clock', 'Có thẻ nhưng lâu chưa quay lại', sig.idle, '/treatment/cards?status=active', 'warn');
      todo('low', 'fa-solid fa-battery-quarter', 'Thẻ sắp hết buổi – mời mua tiếp', sig.low, '/treatment/cards?warn=low', 'ok');
      todo('expiring', 'fa-solid fa-hourglass-half', 'Thẻ sắp hết hạn – nhắc đến làm', sig.expiring, '/treatment/cards?warn=expiring', 'info');
      const r = remote.teleMonth?.rankings.find(x => me && x.staff_id === me);
      kpiFrom(r ? r.booked : null, 86);
      if (r) {
        d.metrics.push({ label: 'Tỉ lệ khách đến', text: `${r.showup}/${r.booked} · ${Math.round(r.showup_rate)}%`, pct: Math.round(r.showup_rate) });
        d.note = `Hạng ${r.rank}/${remote.teleMonth!.rankings.length} telesale`;
      } else if (HOME_DEMO) {
        d.metrics.push({ label: 'Tỉ lệ khách đến', text: '52/86 · 61%', pct: 61, demo: true });
        d.note = 'Hạng 2/8 telesale'; d.noteDemo = true;
      }
    }

    if (role === 'manager') {
      const t = remote.saleToday?.summary;
      if (t) d.stats = [
        { label: 'Khách đến', value: String(t.total_showup), to: '/execution-schedule' },
        { label: 'Chốt', value: `${t.total_closed}`, to: '/list-order' },
        { label: 'Doanh thu', value: moneyShort(t.total_revenue), to: '/revenue' },
      ];
      else if (HOME_DEMO) { d.stats = [{ label: 'Khách đến', value: '42' }, { label: 'Chốt', value: '19' }, { label: 'Doanh thu', value: '186tr' }]; d.statsDemo = true; }
      if (appts?.length) d.schedule = { title: `Lịch hẹn trong ngày (${appts.length})`, rows: toRows(appts) };
      todo('late', 'fa-solid fa-signature', `Lần đến chưa ký quá ${signDays} ngày`, unsignedLate, '/treatment', 'danger');
      todo('overdue', 'fa-solid fa-hand-holding-dollar', 'Nợ thẻ quá hạn', sig.overdue, '/treatment/debts', 'danger', moneyShort(sig.overdueAmount));
      todo('refund', 'fa-solid fa-rotate-left', 'Yêu cầu hoàn tiền thẻ', sig.pendingRefund, '/treatment/cards', 'warn');
      todo('pay', 'fa-solid fa-cash-register', 'Yêu cầu thu đang chờ POS', sig.pendingPay, '/treatment', 'info');
      todo('expiring', 'fa-solid fa-hourglass-half', 'Thẻ sắp hết hạn', sig.expiring, '/treatment/cards?warn=expiring', 'info');
      const m = remote.saleMonth;
      kpiFrom(m ? m.summary.total_revenue : null, 2_100_000_000);
      if (m) {
        d.metrics.push({ label: 'Tỉ lệ chốt tháng', text: `${m.summary.total_closed}/${m.summary.total_showup} · ${Math.round(m.summary.close_rate)}%`, pct: Math.round(m.summary.close_rate) });
        d.note = m.rankings.length ? `Top: ${m.rankings.slice(0, 3).map(x => x.name).join(', ')}` : '';
      } else if (HOME_DEMO) {
        d.metrics.push({ label: 'Tỉ lệ chốt tháng', text: '412/905 · 46%', pct: 46, demo: true });
        d.note = 'Top: Lan, Hùng, Ngọc'; d.noteDemo = true;
      }
    }

    return d;
  }, [role, user, target, remote, loading, cards, payRequests, tcfg, c360docs, photos, records, unsigned, signDays, monthStart]);
}
